import { useState, useCallback, useMemo } from "react";
import { MessageSquare, Star, Download } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import {
  KPICard,
  ExportButton,
  DateRangePicker,
  EmptyState,
  type DateRange,
} from "@/components/admin/shared";
import { useFeedback, useFeedbackSummary } from "@/hooks/admin/useCRM";
import { startOfMonth, endOfMonth, format } from "date-fns";
import type { FeedbackListParams } from "@/lib/admin-api/crm";

// ---------------------------------------------------------------------------
// Star rating display
// ---------------------------------------------------------------------------

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-stone-200"}`}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rating filter buttons
// ---------------------------------------------------------------------------

function RatingFilter({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (r: number | null) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        onClick={() => onChange(null)}
        className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
          value === null
            ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
            : "bg-stone-100 text-stone-600 hover:bg-stone-200"
        }`}
      >
        Toutes les notes
      </button>
      {[5, 4, 3, 2, 1].map((r) => (
        <button
          key={r}
          onClick={() => onChange(value === r ? null : r)}
          className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
            value === r
              ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
              : "bg-stone-100 text-stone-600 hover:bg-stone-200"
          }`}
        >
          {r} <Star className="h-3 w-3 fill-current" />
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export columns
// ---------------------------------------------------------------------------

const EXPORT_COLUMNS = [
  { key: "customer_name", label: "Client" },
  { key: "rating", label: "Note" },
  { key: "comment", label: "Commentaire" },
  { key: "delivery_date", label: "Date livraison" },
  { key: "submitted_at", label: "Date avis" },
] as const;

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Feedback() {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  });
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  const params: FeedbackListParams = useMemo(
    () => ({
      page,
      limit: 20,
      rating: ratingFilter,
      date_from: format(dateRange.from, "yyyy-MM-dd"),
      date_to: format(dateRange.to, "yyyy-MM-dd"),
    }),
    [page, ratingFilter, dateRange],
  );

  const { data, isLoading } = useFeedback(params);
  const { data: summary, isLoading: loadingSummary } = useFeedbackSummary();

  const handleDateChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setPage(1);
  }, []);

  const handleRatingChange = useCallback((r: number | null) => {
    setRatingFilter(r);
    setPage(1);
  }, []);

  const feedback = data?.feedback ?? [];
  const pagination = data?.pagination;

  const exportData = useMemo(
    () =>
      feedback.map((f) => ({
        customer_name: f.customer_name,
        rating: f.rating,
        comment: f.comment ?? "",
        delivery_date: f.delivery_date,
        submitted_at: f.submitted_at,
      })),
    [feedback],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <MessageSquare className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Retours clients
              </h1>
              <p className="text-sm text-stone-500">
                Avis et notes de satisfaction
              </p>
            </div>
          </div>
          <ExportButton
            data={exportData}
            columns={[...EXPORT_COLUMNS]}
            filename="retours-clients"
          />
        </div>

        {/* KPI cards */}
        {loadingSummary ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 animate-pulse">
            {[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-lg bg-stone-100" />)}
          </div>
        ) : summary ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <KPICard
              label="Note moyenne"
              value={`${summary.avg_rating.toFixed(1)}/5`}
              icon={<Star className="h-4 w-4" />}
            />
            <KPICard
              label="NPS estimé"
              value={summary.nps_estimate}
              format="number"
            />
            <KPICard
              label="Total avis"
              value={summary.total_reviews}
              format="number"
              icon={<MessageSquare className="h-4 w-4" />}
            />
            <KPICard
              label="Taux de réponse"
              value={summary.response_rate_percent}
              format="percent"
            />
          </div>
        ) : null}

        {/* Filters */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <DateRangePicker value={dateRange} onChange={handleDateChange} />
          </div>
          <RatingFilter value={ratingFilter} onChange={handleRatingChange} />
        </div>

        {/* Feedback table */}
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          {isLoading ? (
            <div className="space-y-3 p-4 animate-pulse">
              {[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded bg-stone-100" />)}
            </div>
          ) : feedback.length === 0 ? (
            <EmptyState
              icon={<Star className="h-6 w-6" />}
              title="Aucun avis pour cette période"
              description="Ajustez les filtres pour élargir la recherche."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Note</th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Commentaire</th>
                    <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Date livraison</th>
                    <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Date avis</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {feedback.map((f) => (
                    <tr key={f.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-medium text-amber-800">
                            {f.customer_name.charAt(0)}
                          </div>
                          <span className="font-medium text-stone-900">{f.customer_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StarRating rating={f.rating} />
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 max-w-xs">
                        <p className="truncate text-stone-600">
                          {f.comment ?? <span className="italic text-stone-400">—</span>}
                        </p>
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 text-stone-500">
                        {new Date(f.delivery_date).toLocaleDateString("fr-SN")}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 text-stone-500">
                        {new Date(f.submitted_at).toLocaleDateString("fr-SN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.total > pagination.limit && (
          <div className="flex items-center justify-between text-sm text-stone-500">
            <span>
              {((pagination.page - 1) * pagination.limit) + 1}–
              {Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page === 1}
                className="rounded-md border border-stone-200 px-3 py-1.5 text-xs font-medium hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={pagination.page * pagination.limit >= pagination.total}
                className="rounded-md border border-stone-200 px-3 py-1.5 text-xs font-medium hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
